// Review Service - API methods for managing reviews.
// Each call returns the ApiResponse body ({ success, message, data }) and rethrows on failure;
// callers turn errors into text with getErrorMessage (src/utils/apiError.js).
import { reviewsAPI } from '../api/apiService';
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getErrorMessage } from '../utils/apiError';

/** All reviews in the system. */
export const getAllReviews = async () => {
  const response = await reviewsAPI.getAll();
  return response.data;
};

/** Reviews for one product (public). data: [{ id, userName, rating, comment, isVerifiedPurchase, createdAt }] */
export const getProductReviews = async productId => {
  const response = await reviewsAPI.getByProduct(productId);
  return response.data;
};

/** Reviews written by one user. */
export const getUserReviews = async userId => {
  const response = await reviewsAPI.getByUser(userId);
  return response.data;
};

/** Create a review: { productId, comment, rating }. Only signed-in verified buyers can. */
export const createReview = async reviewData => {
  const response = await reviewsAPI.create(reviewData);
  return response.data;
};

/** Update an existing review. */
export const updateReview = async (reviewId, reviewData) => {
  const response = await reviewsAPI.update(reviewId, reviewData);
  return response.data;
};

/** Delete a review. */
export const deleteReview = async reviewId => {
  const response = await reviewsAPI.delete(reviewId);
  return response.data;
};

/** Average rating for a product (public). data: number, 0 when there are no reviews. */
export const getAverageRating = async productId => {
  const response = await reviewsAPI.getAverageRating(productId);
  return response.data;
};

/** Whether a user has already reviewed a product. */
export const checkUserReview = async (userId, productId) => {
  const response = await reviewsAPI.checkUserReview(userId, productId);
  return response.data;
};

/**
 * Whether the signed-in user may review a product (only verified buyers who haven't reviewed it yet).
 * Never throws: GET /Review/can-review/{productId} answers { success, data: { canReview, reason } }.
 * @returns {Promise<{canReview: boolean, reason: string|null}>}
 */
export const canReview = async productId => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.CAN_REVIEW(productId));
    const data = response.data?.data;
    return {
      canReview: Boolean(data?.canReview),
      reason: data?.reason || null,
    };
  } catch (error) {
    return {
      canReview: false,
      reason:
        error.response?.status === 401
          ? 'Sign in to review.'
          : getErrorMessage(error, "We couldn't check whether you can review this project."),
    };
  }
};

const reviewService = {
  getAllReviews,
  getProductReviews,
  getUserReviews,
  createReview,
  updateReview,
  deleteReview,
  getAverageRating,
  checkUserReview,
  canReview,
};

export default reviewService;
